/**
 * The inspector for several selected nodes (the class diagram's box selection): how many, each
 * one by name (a click opens it alone), and deleting them all in one undo.
 */
import { For, createMemo, type JSX } from "solid-js";
import { useApp } from "../../app/context";
import { byId } from "../../shared/analysis";
import { toast } from "../../shared/toast";

/** Several nodes at once. */
export function NodesPanel(props: { ids: string[] }): JSX.Element {
  const { graph, view, actions, access, kinds } = useApp();
  const nodes = createMemo(() => {
    const known = byId(graph.state.graph);
    return props.ids.flatMap((id) => known.get(id) ?? []);
  });
  /** Deletes every selected node and its links (one undo). */
  const remove = () => {
    const ids = nodes().map((n) => n.id);
    void actions
      .removeNodes(ids)
      .then(
        (ok) =>
          ok &&
          (view.selectNode(null),
          toast(`${ids.length} nodes removed. Press Ctrl+Z to undo.`)),
      );
  };
  return (
    <>
      <div class="ihead">
        <h2 data-nodes-title>{nodes().length} nodes selected</h2>
      </div>
      <section class="grp">
        <h3>
          Nodes <span>{nodes().length}</span>
        </h3>
        <ul>
          <For each={nodes()}>
            {(n) => (
              <li>
                <button
                  type="button"
                  class="ref"
                  data-selected-node={n.id}
                  onClick={() => view.selectNode(n.id)}
                >
                  <span>
                    {n.name} <em>{kinds.ofNode(n).label}</em>
                  </span>
                </button>
              </li>
            )}
          </For>
        </ul>
      </section>
      <div class="btnrow">
        <button
          type="button"
          class="btn danger"
          id="nodes-delete"
          disabled={!access.can("editor")}
          onClick={remove}
        >
          Delete {nodes().length} nodes
        </button>
      </div>
      <p class="hint2">
        In the class diagram, dragging one of the selected boxes moves them all;
        "Arrange by group" tidies them, group by group.
        Shift+drag selects boxes, Ctrl+click adds or takes one out.
      </p>
    </>
  );
}
